import ModalAcceptAntecipation from '@components/modals/modal-accept-antecipation/modal-accept-antecipation.component'
import ModalAntecipationOnboarding from '@components/modals/modal-antecipation-onboarding/modal-antecipation-onboarding.component'
import ModalPopupAntecipation from '@components/modals/modal-popup-antecipation/modal-popup-antecipation.component'
import ModalSendNf from '@components/modals/modal-send-nf/modal-send-nf.component'
import { FinancialOperationService, HistoryTransaction, InfluencerTransaction, TioPatinhasService } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  transactions?: Array<HistoryTransaction>
  transactionId?: string
  onCloseFlow?: () => any
}

enum AntecipationSteps {
  modalPopup = 'ModalPopup',
  modalOnboarding = 'ModalOnboarding',
  modalSendNf = 'ModalSendNf',
  modalContract = 'ModalContract',
}

export default ({ transactions, transactionId, onCloseFlow }: Props) => {
  const tioService = useMemo(() => new TioPatinhasService(), [])
  const financialService = useMemo(() => new FinancialOperationService(), [])
  const [loading, setLoading] = useState(false)
  const [transactionsSelected, setTransactionsSelected] = useState<HistoryTransaction | null>(null)
  const [transactionObject, setTransactionObject] = useState<InfluencerTransaction | null>(null)
  const [activeStep, setActiveStep] = useState<AntecipationSteps | null>(null)
  const [toOpenTransactions, setToOpenTransactions] = useState<Array<HistoryTransaction>>([])

  const handleCloseModal = (
    activeStep: AntecipationSteps | null = null,
    transactionsSelected: HistoryTransaction | null = null,
    transactionObject: InfluencerTransaction | null = null
  ) => {
    setTransactionObject(transactionObject)
    setTransactionsSelected(transactionsSelected)
    setActiveStep(activeStep)
    if (onCloseFlow && typeof onCloseFlow === 'function') {
      onCloseFlow()
    }
  }

  useEffect(() => {
    if (!!transactions?.length) {
      setToOpenTransactions(transactions?.filter((transaction) => transaction?.canAnticipate && !transaction?.ignoreAnticipationModal))
    }
  }, [transactions])

  const toOpenPopup = useCallback(
    (openFlow: boolean, toOpenTransactions: Array<HistoryTransaction>) => {
      if (!openFlow && !transactionsSelected) {
        setTransactionsSelected(toOpenTransactions?.[0])
        tioService
          .postShowAntecipation(toOpenTransactions?.[0]?.transactionId)
          .then(() => {
            setActiveStep(AntecipationSteps.modalPopup)
          })
          .catch(() => {
            setTransactionObject(null)
          })
          .finally(() => {
            setToOpenTransactions(toOpenTransactions.slice(1))
          })
      }
    },
    [transactionsSelected, setActiveStep, setToOpenTransactions, tioService]
  )

  useEffect(() => {
    if (toOpenTransactions?.length) {
      toOpenPopup(!!activeStep, toOpenTransactions)
    }
  }, [activeStep, toOpenTransactions, toOpenPopup])

  const setSelected = useCallback((transactionObject: InfluencerTransaction) => {
    const transactionsSelected: HistoryTransaction = {
      transactionId: transactionObject?.transaction?.transactionId,
      netValue: transactionObject?.transaction?.netValue,
      grossValue: transactionObject?.transaction?.grossValue,
      anticipated: !!transactionObject?.transaction?.anticipationContractAccepted,
      dueDate: transactionObject?.transaction?.dueDate.split('T')[0],
      status: transactionObject?.transaction?.transactionStatus,
      canAnticipate: !!transactionObject?.anticipation?.authorization,
      paymentType: transactionObject?.transaction?.paymentType,
      ignoreAnticipationModal: false,
      nfId: transactionObject?.nfs?.length ? 'haveNfId' : '',
      anticipation: {
        aliquot: transactionObject?.transaction?.anticipationAliquot,
        value: transactionObject?.transaction?.anticipationValue,
      },
      anticipationDueDate: transactionObject?.transaction?.anticipationDueDate,
    }
    setTransactionsSelected(transactionsSelected)
  }, [])

  const getTransactionDetails = useCallback(
    (transactionId?: string) => {
      setLoading(true)
      financialService
        .getTransactionDetails(transactionId || transactionsSelected?.transactionId || '')
        .then((response) => {
          setTransactionObject(response)
          setActiveStep(transactionId ? AntecipationSteps.modalPopup : AntecipationSteps.modalSendNf)
          if (transactionId) {
            setSelected(response)
          }
        })
        .catch(() => {
          setTransactionObject(null)
          setActiveStep(AntecipationSteps.modalPopup)
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [financialService, transactionsSelected, setSelected]
  )

  useEffect(() => {
    if (transactionId && !transactionObject) {
      getTransactionDetails(transactionId)
    }
  }, [transactionObject, transactionId, getTransactionDetails])

  if (!activeStep) {
    return null
  }

  return (
    <div>
      <ModalPopupAntecipation
        openModal={activeStep === AntecipationSteps.modalPopup}
        transactionObject={transactionsSelected}
        openOnboarding={() => setActiveStep(AntecipationSteps.modalOnboarding)}
        nextStep={() => {
          if (!!transactionsSelected?.nfId) {
            setActiveStep(AntecipationSteps.modalContract)
          } else if (!transactionObject?.transaction?.transactionId) {
            getTransactionDetails()
          } else {
            setActiveStep(AntecipationSteps.modalSendNf)
          }
        }}
        closeModal={() => handleCloseModal()}
        loading={loading}
      />
      <ModalAntecipationOnboarding
        openModal={activeStep === AntecipationSteps.modalOnboarding}
        transactionObject={transactionsSelected}
        closeModal={() => setActiveStep(AntecipationSteps.modalPopup)}
      />
      <ModalSendNf
        open={activeStep === AntecipationSteps.modalSendNf}
        transactionObject={transactionObject}
        afterSubmit={() => setActiveStep(AntecipationSteps.modalContract)}
        closeModal={() => setActiveStep(AntecipationSteps.modalPopup)}
      />
      <ModalAcceptAntecipation
        openModal={activeStep === AntecipationSteps.modalContract}
        transactionId={transactionsSelected?.transactionId}
        closeModal={() => {
          handleCloseModal()
        }}
      />
    </div>
  )
}
